import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Motor do tutorial guiado. Cada tela só descreve os capítulos e passos (veja
 * `screens/creditos/creditosTour.js` e a seção de tutoriais do design-system.md).
 *
 * definition: {
 *   id, version, title, intro,
 *   notice(ctx): aviso no guia, para explicar por que há tutoriais bloqueados,
 *   chapters: [{
 *     key, title, summary, minutes, steps: [step],
 *     when(ctx): o capítulo só aparece se for verdadeiro,
 *     locked(ctx): aparece no guia, mas não abre, porque a tela não tem o que mostrar,
 *     offer: { title, text }: oferta discreta; vale a primeira de um capítulo disponível,
 *   }],
 *   glossary: [{ term, text }],
 * }
 * step: {
 *   id,
 *   target: seletor ou lista de seletores (o destaque cobre todos); sem alvo, o cartão fica no centro,
 *   title, body (texto ou função do contexto), insight (função: frase com os dados reais),
 *   when(ctx): o passo só existe se for verdadeiro,
 *   before(ctx): prepara a tela (trocar de aba, abrir um modal),
 *   after(ctx): desfaz o que before abriu, ao sair do passo ou do tutorial,
 *   action: { hint, done(ctx), perform(ctx) }: passo em que a pessoa faz. Avança sozinho quando done dá certo,
 *   placement, noScroll,
 *   alwaysScroll: rola até o alvo mesmo com ele na janela, para conteúdo que rola dentro de um modal,
 * }
 *
 * O progresso fica no navegador, por usuário e por versão do tutorial. Ao mudar os
 * passos de um jeito relevante, suba `version`: a oferta inicial volta a aparecer.
 */

const storageKey = (def) => {
  let user = "anon";
  try {
    const raw = JSON.parse(localStorage.getItem("vidal_user") || "null");
    user = raw?.id || raw?._id || "anon";
  } catch { /* segue anônimo */ }
  return `vidal_tour_${def.id}_v${def.version}_${user}`;
};

const EMPTY_PROGRESS = { done: [], offersSeen: [], resume: null };

const isVisible = (chapter, ctx) => !chapter.when || chapter.when(ctx);
const isOpen = (chapter, ctx) => isVisible(chapter, ctx) && !(chapter.locked && chapter.locked(ctx));
const stepsOf = (chapter, ctx) => chapter.steps.filter((s) => !s.when || s.when(ctx));

const readProgress = (def) => {
  try {
    return { ...EMPTY_PROGRESS, ...JSON.parse(localStorage.getItem(storageKey(def)) || "{}") };
  } catch {
    return EMPTY_PROGRESS;
  }
};

export default function useGuidedTour(definition, ctx) {
  const [progress, setProgress] = useState(() => readProgress(definition));
  const [active, setActive] = useState(null); // { chapterKey, index, ended, dir }
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const save = useCallback((patch) => {
    setProgress((prev) => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(storageKey(definition), JSON.stringify(next)); } catch { /* preferência só desta sessão */ }
      return next;
    });
  }, [definition]);

  const chapter = active ? definition.chapters.find((c) => c.key === active.chapterKey) : null;

  // Só os passos que fazem sentido com os dados atuais
  const steps = useMemo(() => {
    return chapter ? stepsOf(chapter, ctx) : [];
  }, [chapter, ctx]);

  const step = active && !active.ended ? steps[active.index] || null : null;

  // A oferta da situação atual da tela: sem dados, a primeira análise; com dados, a visão geral
  const offerChapter = definition.chapters.find((c) => c.offer && isOpen(c, ctx)) || null;
  const offerKey = offerChapter?.key;

  const start = useCallback((chapterKey, index = 0) => {
    const target = definition.chapters.find((c) => c.key === chapterKey);
    if (!target) return;
    // O progresso guardado pode ser de quando havia mais passos
    const count = stepsOf(target, ctxRef.current).length;
    setProgress((prev) => {
      const offersSeen = [...new Set([...prev.offersSeen, chapterKey, ...(offerKey ? [offerKey] : [])])];
      const nextProgress = { ...prev, offersSeen };
      try { localStorage.setItem(storageKey(definition), JSON.stringify(nextProgress)); } catch { /* idem */ }
      return nextProgress;
    });
    setActive({ chapterKey, index: Math.max(0, Math.min(index, count - 1)), ended: false, dir: 1 });
  }, [definition, offerKey]);

  const close = useCallback(() => {
    setActive((cur) => {
      if (cur && !cur.ended) save({ resume: { chapterKey: cur.chapterKey, index: cur.index } });
      return null;
    });
  }, [save]);

  const finishChapter = useCallback(() => {
    setActive((cur) => {
      if (!cur) return cur;
      setProgress((prev) => {
        const done = prev.done.includes(cur.chapterKey) ? prev.done : [...prev.done, cur.chapterKey];
        const next = { ...prev, done, resume: null };
        try { localStorage.setItem(storageKey(definition), JSON.stringify(next)); } catch { /* idem */ }
        return next;
      });
      return { ...cur, ended: true };
    });
  }, [definition]);

  const next = useCallback(() => {
    setActive((cur) => {
      if (!cur || cur.ended) return cur;
      return { ...cur, index: cur.index + 1, dir: 1 };
    });
  }, []);

  const back = useCallback(() => {
    setActive((cur) => {
      if (!cur || cur.index === 0) return cur;
      return { ...cur, index: cur.index - 1, dir: -1, ended: false };
    });
  }, []);

  // Passou do último passo: fecha o capítulo
  useEffect(() => {
    if (active && !active.ended && steps.length > 0 && active.index >= steps.length) finishChapter();
  }, [active, steps.length, finishChapter]);

  // A tela mudou e o capítulo ficou sem passos: fecha em vez de escurecer a tela sem cartão
  useEffect(() => {
    if (active && !active.ended && steps.length === 0) setActive(null);
  }, [active, steps.length]);

  // Prepara a tela ao entrar em cada passo e desfaz ao sair. As duas chamadas caem no
  // mesmo ciclo, então um modal aberto em passos seguidos não pisca.
  const stepId = step?.id;
  useEffect(() => {
    const current = step;
    if (current?.before) current.before(ctxRef.current);
    return () => { if (current?.after) current.after(ctxRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId]);

  // Alvo não apareceu: segue no mesmo sentido, para o tutorial nunca travar
  const skipMissing = useCallback(() => {
    setActive((cur) => {
      if (!cur || cur.ended) return cur;
      const dir = cur.dir || 1;
      if (dir < 0 && cur.index === 0) return { ...cur, dir: 1, index: 1 };
      return { ...cur, index: cur.index + dir };
    });
  }, []);

  // Passo de ação: avança sozinho quando a pessoa faz
  const actionDone = !!(step?.action && step.action.done(ctx));
  useEffect(() => {
    if (actionDone) {
      const timer = setTimeout(next, 450);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [actionDone, next, stepId]);

  const perform = useCallback(() => {
    if (step?.action) step.action.perform(ctxRef.current);
  }, [step]);

  const dismissOffer = useCallback(() => {
    if (offerKey) save({ offersSeen: [...new Set([...progress.offersSeen, offerKey])] });
  }, [offerKey, progress.offersSeen, save]);
  const reset = useCallback(() => {
    const clean = { ...EMPTY_PROGRESS, offersSeen: definition.chapters.map((c) => c.key) };
    setProgress(clean);
    try { localStorage.setItem(storageKey(definition), JSON.stringify(clean)); } catch { /* idem */ }
  }, [definition]);

  return {
    definition,
    ctx,
    progress,
    active,
    chapter,
    step,
    steps,
    index: active ? active.index : 0,
    ended: !!active?.ended,
    offer: offerChapter?.offer || null,
    offerKey,
    showOffer: !active && !!ctx.ready && !!offerChapter && !progress.offersSeen.includes(offerKey) && !progress.done.includes(offerKey),
    // Disponibilidade por capítulo, sempre calculada na hora
    chapters: definition.chapters.filter((c) => isVisible(c, ctx)).map((c) => ({ ...c, isLocked: !isOpen(c, ctx) })),
    notice: definition.notice ? definition.notice(ctx) : null,
    start,
    next,
    back,
    close,
    perform,
    actionDone,
    skipMissing,
    dismissOffer,
    reset,
    pendingChapters: definition.chapters.filter((c) => isOpen(c, ctx) && !progress.done.includes(c.key)),
  };
}
